/**
 * A popover anchored to a trigger, rendered through a PORTAL — Step 42.
 *
 * Extracted because the queue has two of these (the property cells and the
 * assignment picker) and the positioning is the fiddly part: two copies would
 * drift, and the second copy is always the one that forgets to close on
 * scroll.
 *
 * **Why a portal and `position: fixed`.** The queue's list is an
 * `overflow-auto` box on both axes, which clips absolutely-positioned
 * descendants: a menu on any row but the top few would be cut off, or would
 * grow the scroll region so the table had to scroll to reach its own menu.
 *
 * The cost of `fixed` is that the popover does NOT travel with the cell it
 * points at, so any scroll has to dismiss it rather than leave a menu hanging
 * over the wrong row. That listener is on the window in the CAPTURE phase,
 * because the element actually scrolling is an ancestor and scroll events do
 * not bubble.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface AnchoredPopover {
  open: boolean;
  setOpen: (v: boolean) => void;
  /** `null` until the first measurement — render the popover only once set,
   *  or it flashes at the top-left corner for a frame. */
  pos: { top: number; left: number; flip: boolean } | null;
  // `MutableRefObject`, not `RefObject`: this React's `RefObject<T | null>`
  // is not assignable to a JSX `ref`, and widening at the call site would push
  // a cast into every consumer.
  triggerRef: React.MutableRefObject<HTMLButtonElement | null>;
  popRef: React.MutableRefObject<HTMLDivElement | null>;
}

export function useAnchoredPopover(width: number, maxHeight: number): AnchoredPopover {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<AnchoredPopover['pos']>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom;
    // Flip up only when there is genuinely more room above: flipping into an
    // even smaller gap just moves the clipping.
    const flip = below < maxHeight && r.top > below;
    setPos({
      top: flip ? r.top - 4 : r.bottom + 4,
      // Clamped to the viewport so a right-hand column's menu cannot open off
      // the edge of the screen.
      left: Math.min(Math.max(8, r.left), window.innerWidth - width - 8),
      flip,
    });
  }, [width, maxHeight]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!popRef.current?.contains(t) && !triggerRef.current?.contains(t)) setOpen(false);
    };
    const gone = () => setOpen(false);
    document.addEventListener('mousedown', away);
    window.addEventListener('scroll', gone, true);
    window.addEventListener('resize', gone);
    return () => {
      document.removeEventListener('mousedown', away);
      window.removeEventListener('scroll', gone, true);
      window.removeEventListener('resize', gone);
    };
  }, [open]);

  return { open, setOpen, pos, triggerRef, popRef };
}
